import React from 'react'
import { makeStyles } from '@material-ui/styles'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import { ImpersonateUserDialog } from 'lib/auth'
import { useIsTablet } from 'lib/hooks'
import { getInitials } from 'lib/text-display'
import { formatCoachRole } from './utils'

const useStyles = makeStyles(theme => ({
  info: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  coachAvatar: {
    height: 36,
    width: 36,
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    fontSize: '13px',
    fontWeight: 500,
  },
  text: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.15px',
    textAlign: 'left',
  },
  roleChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 9999,
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '16px',
    whiteSpace: 'nowrap',
  },
  roleChipOwner: {
    backgroundColor: '#E0EAFF',
    color: '#001C57',
  },
  roleChipStaff: {
    backgroundColor: '#EBF0F2',
    color: '#172024',
  },
  actions: {
    textAlign: 'right',
    width: 1,
    whiteSpace: 'nowrap',
  },
  impersonateButton: {
    height: 32,
    minHeight: 32,
    borderRadius: 8,
    padding: '0 12px',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '16px',
    borderColor: '#C4C7C9',
    color: '#172024',
    backgroundColor: theme.palette.common.white,
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#EBEBEB',
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
}))

const AcademyCoachRow = ({ employment }) => {
  const classes = useStyles()
  const isTablet = useIsTablet()
  const { role, coach, isImpersonatable } = employment
  const displayName = coach?.name || coach?.email || 'Unknown'
  const roleLabel = formatCoachRole(role)
  const isOwner = role === 'OWNER'

  return (
    <TableRow data-testid={`academy-coach-row-${employment.id}`}>
      <TableCell className={classes.info}>
        <Box className={classes.nameCell}>
          <Avatar
            className={classes.coachAvatar}
            src={coach?.coachProfile?.profilePhoto}
            alt={displayName}
          >
            {getInitials(displayName) || 'U'}
          </Avatar>
          <Box className={classes.info}>
            <Typography className={classes.text} noWrap>
              {displayName}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <span
          className={`${classes.roleChip} ${
            isOwner ? classes.roleChipOwner : classes.roleChipStaff
          }`}
        >
          {roleLabel}
        </span>
      </TableCell>
      {!isTablet && (
        <TableCell className={classes.info}>
          <Typography className={classes.text} noWrap>
            {coach?.email}
          </Typography>
        </TableCell>
      )}
      <TableCell className={classes.actions}>
        {isImpersonatable !== false && coach?.id && (
          <ImpersonateUserDialog
            trigger={
              <Button
                variant="outlined"
                size="small"
                className={classes.impersonateButton}
                aria-label={`Impersonate ${displayName}`}
                data-testid={`academy-coach-impersonate-${employment.id}`}
              >
                Impersonate
              </Button>
            }
            user={{
              userType: 'Coach',
              id: coach.id,
              name: displayName,
            }}
            hideSensitiveInformation
          />
        )}
      </TableCell>
    </TableRow>
  )
}

export default AcademyCoachRow
